import type { WritableStateSource } from '@ngrx/signals'
import { patchState, signalStore } from '@ngrx/signals'
import type { EntityId, EntityMap } from '@ngrx/signals/entities'
import {
  addEntities,
  removeAllEntities,
  removeEntities,
  setAllEntities,
  updateEntities,
  withEntities
} from '@ngrx/signals/entities'
import type { Signal, Type } from '@angular/core'
import { computed, inject, Injector, signal, untracked } from '@angular/core'
import {
  coerceArray,
  getNullableOverrides,
  isPersistStateEntityDeleteStorage,
  isFunction,
  normalizeDateValue,
  normalizeSyncFilters
} from '@awork/core/state/signal-store/helpers'
import { isStoredEntityOutdated, syncStore } from '@awork/core/state/signal-store/sync-store'
import { EntitySignalStoreHistory } from '@awork/core/state/signal-store/entitySignalStoreHistory'
import { SignalStoreService } from '@awork/core/state/signal-store/signalStore.service'
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit'
import { withEntityStorage } from '@awork/core/state/signal-store/withStorage'
import { withNoop } from '@awork/core/state/signal-store/withNoop'
import type {
  EntityProps,
  GenericEntity,
  SyncFilter,
  SyncOptions,
  SyncResponse,
  TrackedEntitiesRemovedEvent
} from '@awork/core/state/signal-store/types'
import { SYNC_FILTERS_CLEANUP_THRESHOLD } from '@awork/core/state/signal-store/types'
import type { Observable } from 'rxjs'
import { Subject, take } from 'rxjs'
import { PersistState } from '@awork/core/state/signal-store/persistState'
import { deepEqual } from '@awork/_shared/functions/lodash'

type StoreType<Entity> = Type<
  {
    entityMap: Signal<EntityMap<Entity>>
    ids: Signal<EntityId[]>
    entities: Signal<Entity[]>
  } & WritableStateSource<{
    entityMap: EntityMap<Entity>
    ids: EntityId[]
  }>
>

export type Mapper<T> = (entity: T) => T

export interface StoreOptions<Entity> {
  name: string
  resettable?: boolean
  entityConstructor?: Mapper<Entity>
  persist?: boolean
  persistDelay?: number
  persistActiveState?: boolean
  excludePersistProps?: (keyof Entity)[]
  nullableProps?: (keyof Entity)[]
}

export interface ApiSyncOptions {
  updateLastSync?: boolean
  /**
   * The value of `getResetGeneration()` at the time the sync request was issued.
   * When provided, the response is only applied if the store has not been reset since.
   */
  issuedAtGeneration?: number
  /**
   * Applies the response even when the store is currently loading or has no last sync date.
   * Only set this when the store was intentionally reset and the response is a full snapshot
   * that replaces its state within the same flow (see `TaskService.resetTaskStoreFromFullSync`).
   */
  skipStaleCheck?: boolean
}

export interface EntityPersistenceDelegate {
  /**
   * Records that the persisted store should match the provided entity ids exactly.
   * @param {string[]} ids
   */
  replace(ids: string[]): void

  /**
   * Records entity rows that may need to be upserted.
   * @param {string[]} ids
   */
  upsert(ids: string[]): void

  /**
   * Records entity rows that may need to be deleted.
   * @param {string[]} ids
   */
  delete(ids: string[]): void

  /**
   * Records that persistence metadata may need to be updated.
   */
  metaChange(): void

  /**
   * Clears queued persistence work after the store was reset.
   */
  reset(): void
}

/**
 * Signal store for entities.
 * Provides functions to set, add, update and delete entities.
 *
 * ### Usage example
 * ```ts
 * @Injectable({ providedIn: 'root' })
 * export class UserStore extends EntitySignalStore<User> {
 *   constructor() {
 *     super({
 *       resettable: true,
 *       name: 'user',
 *       entityConstructor: user => new User(user, 'User store')
 *     })
 *   }
 * }
 * ```
 */
export class EntitySignalStore<Entity extends GenericEntity> {
  private StoreFactory: StoreType<Entity>
  private store: InstanceType<typeof this.StoreFactory>

  private storeOptions: StoreOptions<Entity>

  private readonly activeId = signal<string>(null)
  protected readonly isLoading = signal(true)
  private readonly error = signal(null)
  private readonly lastSync = signal<Date>(null)
  private readonly loadedOn = signal<Date>(null)
  private readonly syncFilters = signal<SyncFilter[]>([])
  private resetGeneration = 0

  /** Emits when entities are removed while a sync filter still tracks them */
  readonly trackedEntitiesRemoved = new Subject<TrackedEntitiesRemovedEvent>()

  readonly history: EntitySignalStoreHistory<Entity>

  private signalStoreService = inject(SignalStoreService<Entity>)
  private injector = inject(Injector)

  private readonly DEFAULT_STORE_OPTIONS: Partial<StoreOptions<Entity>> = {
    resettable: true,
    persist: true,
    persistActiveState: true,
    entityConstructor: entity => entity
  }

  private integrateDevtools: boolean

  protected restored$: Observable<void>
  private persistenceDelegate: EntityPersistenceDelegate | null = null
  private hasCompletedRestoreTracking = true
  private isRestoreHydrating = false
  private pendingRestoreOperations: (() => void)[] = []
  private shouldRestorePersistedState = true

  constructor(storeOptions?: StoreOptions<Entity>) {
    this.applyStoreOptions(storeOptions)
    this.createStore()

    this.history = new EntitySignalStoreHistory(this)
    this.signalStoreService.registerStore(this.storeOptions.name, this)
    this.restored$ = this.signalStoreService.selectOnStoreRestored(this.storeOptions.name)

    this.restored$.pipe(take(1)).subscribe(() => {
      this.performSyncFiltersCleanup()
    })
  }

  /**
   * Applies the store options, merging them with the default store options
   * @param {StoreOptions<Entity>} storeOptions
   */
  private applyStoreOptions(storeOptions: StoreOptions<Entity>): void {
    this.storeOptions = {
      ...this.DEFAULT_STORE_OPTIONS,
      ...storeOptions
    }
  }

  /**
   * Creates the signal store
   */
  private createStore(): void {
    this.integrateDevtools = localStorage.getItem('awReduxDevtools') === 'true'

    this.StoreFactory = signalStore(
      { protectedState: false },
      this.integrateDevtools ? withDevtools(this.storeOptions.name) : withNoop(),
      withEntities<Entity>(),
      this.storeOptions.persist
        ? withEntityStorage(
            this,
            {
              key: this.storeOptions.name,
              persistDelay: this.storeOptions.persistDelay,
              persistActiveState: this.storeOptions.persistActiveState,
              excludeProps: this.storeOptions.excludePersistProps
            },
            this.injector
          )
        : withNoop()
    )
    this.store = new this.StoreFactory()
  }

  /**
   * Gets the entity constructor
   * @returns {Mapper<Entity>}
   */
  getEntityConstructor(): Mapper<Entity> {
    return this.storeOptions.entityConstructor
  }

  /**
   * Gets the store options
   * @returns {StoreOptions<Entity>}
   */
  getStoreOptions(): StoreOptions<Entity> {
    return this.storeOptions
  }

  get entities(): Signal<Entity[]> {
    return this.store.entities
  }

  get mappedEntities(): Signal<Entity[]> {
    return computed(() => this.store.entities().map(this.storeOptions.entityConstructor))
  }

  get entityMap(): Signal<Record<string, Entity>> {
    return this.store.entityMap
  }

  get ids(): Signal<string[]> {
    return this.store.ids as Signal<string[]>
  }

  /**
   * Registers the persistence delegate used by storage integration.
   * @param {EntityPersistenceDelegate | null} persistenceDelegate
   */
  registerPersistenceDelegate(persistenceDelegate: EntityPersistenceDelegate | null): void {
    this.persistenceDelegate = persistenceDelegate
  }

  /**
   * Marks this store as fully restored after hydration completed.
   */
  markAsRestored(): void {
    this.signalStoreService.markStoreAsRestored(this.storeOptions.name)
  }

  /**
   * Determines whether persisted state should still be applied.
   * @returns {boolean}
   */
  canApplyPersistedRestore(): boolean {
    return this.shouldRestorePersistedState
  }

  /**
   * Starts tracking local write operations until persistence restore completes.
   */
  beginRestoreTracking(): void {
    this.shouldRestorePersistedState = true
    this.hasCompletedRestoreTracking = false
    this.isRestoreHydrating = false
    this.pendingRestoreOperations = []
  }

  /**
   * Marks that persisted state is currently being applied to the store.
   */
  startRestoreHydration(): void {
    this.isRestoreHydrating = true
  }

  /**
   * Finishes restore tracking and replays queued local write operations on top of restored state.
   * @param {boolean} hydrated
   */
  finishRestoreTracking(hydrated: boolean): void {
    this.isRestoreHydrating = false
    this.hasCompletedRestoreTracking = true

    if (!hydrated) {
      this.pendingRestoreOperations = []

      return
    }

    const pendingRestoreOperations = [...this.pendingRestoreOperations]

    this.pendingRestoreOperations = []
    pendingRestoreOperations.forEach(replayOperation => replayOperation())
  }

  /**
   * Hook that is called before adding an entity to the store
   * @param {Entity} newEntity
   * @returns {Entity}
   * @internal
   */
  preAddEntity(newEntity: Entity): Entity {
    return newEntity
  }

  /**
   * Applies the preAddEntity hook to the given entities before adding them to the store
   * @param {Entity[]} entities
   * @returns {Entity[]}
   */
  private applyPreAddEntity(entities: Entity[]): Entity[] {
    return entities.map(this.preAddEntity)
  }

  /**
   * Hook that is called before updating an entity in the store
   * @param {Entity} prevEntity
   * @param {Partial<Entity>} nextEntity
   * @returns {Partial<Entity>}
   * @internal
   */
  preUpdateEntity(prevEntity: Entity, nextEntity: Partial<Entity>): Partial<Entity> {
    return nextEntity
  }

  /**
   * Applies the preUpdateEntity hook to the given entity before updating it in the store
   * @param {Entity} prevEntity
   * @param {Partial<Entity> | ((entity: Entity) => Partial<Entity>)} newState
   * @param {boolean} useNullableProps
   * @returns {Partial<Entity>}
   */
  private applyPreUpdateEntity(
    prevEntity: Entity,
    newState: Partial<Entity> | ((entity: Entity) => Partial<Entity>),
    useNullableProps: boolean
  ): Partial<Entity> {
    const nextEntity = isFunction(newState) ? newState(prevEntity) : newState
    const nullableOverrides = useNullableProps ? getNullableOverrides(nextEntity, this.storeOptions.nullableProps) : {}

    const mergedNextEntity: Partial<Entity> = {
      ...prevEntity,
      ...nextEntity,
      ...nullableOverrides
    }

    return this.preUpdateEntity(prevEntity, mergedNextEntity)
  }

  /**
   * Fills the store with the given entities.
   * Also sets the loading state to false.
   * @param {Entity[]} entities
   * @param {string} [action=this.storeOptions.name + ': set']
   */
  set(entities: Entity[], action = this.storeOptions.name + ': set'): void {
    const newEntities = this.applyPreAddEntity(entities)

    this.integrateDevtools
      ? updateState(this.store, action, setAllEntities(newEntities))
      : patchState(this.store, setAllEntities(newEntities))

    this.persistenceDelegate?.replace(newEntities.map(entity => entity.id))
    this.setLoading(false)
    this.queueRestoreOperation(() => {
      this.set(entities, action)
    })
  }

  /**
   * Sets the active entity id
   * @param {string} id
   */
  setActive(id: string): void {
    this.activeId.set(id)
    this.persistenceDelegate?.metaChange()
  }

  /**
   * Gets the active entity id
   * @returns {Signal<string>}
   */
  getActive(): Signal<string> {
    return this.activeId
  }

  /**
   * Sets the loading state
   * @param {boolean} isLoading
   * @param {Date | string | null} loadedOn
   */
  setLoading(isLoading: boolean, loadedOn?: Date | string | null): void {
    const wasLoading = this.isLoading()
    const normalizedLoadedOn = normalizeDateValue(loadedOn)

    if (wasLoading === isLoading) {
      if (!isLoading && normalizedLoadedOn) {
        this.loadedOn.set(normalizedLoadedOn)
        this.persistenceDelegate?.metaChange()
      }

      return
    }

    this.isLoading.set(isLoading)

    if (isLoading) {
      this.loadedOn.set(null)
      this.persistenceDelegate?.metaChange()

      return
    }

    this.loadedOn.set(normalizedLoadedOn || new Date())
    this.persistenceDelegate?.metaChange()
  }

  /**
   * Gets the loading state
   * @returns {Signal<boolean>}
   */
  getIsLoading(): Signal<boolean> {
    return this.isLoading
  }

  /**
   * Gets the loaded on date
   * @returns {Signal<Date | null>}
   */
  getLoadedOn(): Signal<Date | null> {
    return this.loadedOn
  }

  /**
   * Sets the error state
   * @param {Error} error
   */
  setError(error: Error): void {
    this.error.set(error)
    this.persistenceDelegate?.metaChange()
  }

  /**
   * Gets the error state
   * @returns {Signal<Error>}
   */
  getError(): Signal<Error> {
    return this.error
  }

  /**
   * Sets the last sync date
   * @param {Date | string | null} lastSync
   */
  setLastSync(lastSync: Date | string | null): void {
    this.lastSync.set(normalizeDateValue(lastSync))
    this.persistenceDelegate?.metaChange()
  }

  /**
   * Gets the last sync date
   * @returns {Signal<Date>}
   */
  getLastSync(): Signal<Date> {
    return this.lastSync
  }

  /**
   * Gets the current reset generation for a sync request.
   * Capture this when issuing a sync request and pass it as `ApiSyncOptions.issuedAtGeneration`
   * so a response from before an intervening reset is dropped even if the store state is valid again.
   * @returns {number} Current reset generation.
   */
  getResetGeneration(): number {
    return this.resetGeneration
  }

  /**
   * Sets the sync filters
   * @param {SyncFilter[]} syncFilters
   */
  setSyncFilters(syncFilters: SyncFilter[]): void {
    this.syncFilters.set(normalizeSyncFilters(syncFilters))
    this.persistenceDelegate?.metaChange()
  }

  /**
   * Gets the sync filters
   * @returns {Signal<SyncFilter[]>}
   */
  getSyncFilters(): Signal<SyncFilter[]> {
    return this.syncFilters
  }

  /**
   * Adds the given entity or entities to the store
   * @param {Entity | Entity[]} entity
   * @param {string} [action=this.storeOptions.name + ': add']
   */
  add(entity: Entity | Entity[], action = this.storeOptions.name + ': add'): void {
    const newEntities = this.applyPreAddEntity(coerceArray(entity))

    this.integrateDevtools
      ? updateState(this.store, action, addEntities(newEntities))
      : patchState(this.store, addEntities(newEntities))

    this.persistenceDelegate?.upsert(newEntities.map(newEntity => newEntity.id))
    this.queueRestoreOperation(() => {
      this.add(entity, action)
    })
  }

  /**
   * Updates the entity or entities in the store
   * @param {string | string[]} id
   * @param {Partial<Entity>} newState
   * @param {string} action
   * @param {boolean} useNullableProps
   * @example
   * update('1', { value: 'newValue' })
   * update(['1', '2'], { value: 'newValue' })
   * update('1', entity => ({ value: entity.value + 'newValue' }))
   * update(entity => entity.value === 'value1', { value: 'newValue' })
   */
  update(id: string | string[], newState: Partial<Entity>, action?: string, useNullableProps?: boolean): void
  update(
    id: string | string[],
    newStateFn: (entity: Entity) => Partial<Entity>,
    action?: string,
    useNullableProps?: boolean
  ): void
  update(
    id: string | string[],
    newStateOrFn: Partial<Entity> | ((entity: Entity) => Partial<Entity>),
    action?: string,
    useNullableProps?: boolean
  ): void
  update(
    predicate: (entity: Entity) => boolean,
    newState: Partial<Entity>,
    action?: string,
    useNullableProps?: boolean
  ): void
  update(
    predicate: (entity: Entity) => boolean,
    newStateFn: (entity: Entity) => Partial<Entity>,
    action?: string,
    useNullableProps?: boolean
  ): void
  update(
    idOrPredicate: string | string[] | ((entity: Entity) => boolean),
    newStateOrFn: Partial<Entity> | ((entity: Entity) => Partial<Entity>),
    action = this.storeOptions.name + ': update',
    useNullableProps?: boolean
  ): void {
    let ids: string[]

    if (isFunction(idOrPredicate)) {
      ids = this.store
        .entities()
        .filter(idOrPredicate)
        .map(entity => entity.id)
    } else {
      ids = coerceArray(idOrPredicate)
    }

    if (!ids.length) {
      if (isFunction(idOrPredicate)) {
        this.queueRestoreOperation(() => {
          this.replayUpdateOperation(idOrPredicate, newStateOrFn, action, useNullableProps)
        })
      }

      return
    }

    const entityMap = this.store.entityMap()
    const missingIds = ids.filter(id => !entityMap[id])
    const mergedById = new Map<string, Partial<Entity>>()

    ids.forEach(id => {
      const prevEntity = entityMap[id]

      if (!prevEntity || mergedById.has(id)) {
        return
      }

      mergedById.set(id, this.applyPreUpdateEntity(prevEntity, newStateOrFn, useNullableProps))
    })

    const changedIds = ids.filter(id => {
      const prevEntity = entityMap[id]
      const mergedEntity = mergedById.get(id)

      // The merge creates a plain object, so ignore the stored entity's top-level prototype.
      return !!prevEntity && !!mergedEntity && !deepEqual({ ...prevEntity }, { ...mergedEntity })
    })

    if (changedIds.length) {
      this.integrateDevtools
        ? updateState(
            this.store,
            action,
            updateEntities({
              ids: changedIds,
              changes: prevEntity => mergedById.get(prevEntity.id)
            })
          )
        : patchState(
            this.store,
            updateEntities({
              ids: changedIds,
              changes: prevEntity => mergedById.get(prevEntity.id)
            })
          )
      this.persistenceDelegate?.upsert(changedIds)
    }

    if (changedIds.length || missingIds.length) {
      this.queueRestoreOperation(() => {
        this.replayUpdateOperation(idOrPredicate, newStateOrFn, action, useNullableProps)
      })
    }
  }

  /**
   * Adds or updates the entity or entities in the store
   * @param {string | string[]} id
   * @param {Partial<Entity> | ((entity: Entity) => Partial<Entity>)} newStateOrFn
   * @param {string} [action=this.storeOptions.name + ': upsert']
   * @example
   * upsert('1', { value: 'newValue' })
   * upsert(['1', '2'], { value: 'newValue' })
   * upsert('1', entity => ({ value: entity.value + 'newValue' }))
   */
  upsert(
    id: string | string[],
    newStateOrFn: Partial<Entity> | ((entity: Entity) => Partial<Entity>),
    action = this.storeOptions.name + ': upsert'
  ): void {
    const ids = coerceArray(id)

    if (!ids.length) {
      return
    }

    const entityMap = this.store.entityMap()
    const predicate = (isUpdate: boolean) => (id: string) => (isUpdate ? !!entityMap[id] : !entityMap[id])

    const updateIds = ids.filter(predicate(true))
    const newEntities = ids.filter(predicate(false)).map(id => {
      return {
        id,
        ...(isFunction(newStateOrFn) ? newStateOrFn(this.store.entityMap()[id]) : newStateOrFn)
      } as Entity
    })

    this.update(updateIds, newStateOrFn, action)
    this.add(newEntities, action)
  }

  /**
   * Adds or updates the entities in the store
   * @param {Entity[]} entities
   * @param {string} [action=this.storeOptions.name + ': upsertMany']
   */
  upsertMany(entities: Entity[], action = this.storeOptions.name + ': upsertMany'): void {
    const entityMap = this.store.entityMap()
    const isNewEntity = (id: string) => !entityMap[id]

    const updatedEntitiesMap = new Map<string, Entity>()
    const updateIds: string[] = []
    const newEntities: Entity[] = []

    entities.forEach(entity => {
      if (isNewEntity(entity.id)) {
        newEntities.push(entity)

        return
      } else {
        updatedEntitiesMap.set(entity.id, entity)
        updateIds.push(entity.id)
      }
    })

    this.update(updateIds, entity => updatedEntitiesMap.get(entity.id), action)
    this.add(newEntities, action)
  }

  /**
   * Removes the entity or entities from the store
   * @param {string | string[]} id
   * @param {string} action
   * @example
   * remove('1')
   * remove(['1', '2'])
   * remove(entity => entity.value === 'value1')
   */
  remove(id: string | string[], action?: string): void
  remove(predicate: (entity: Entity) => boolean, action?: string): void
  remove(
    idOrPredicate: string | string[] | ((entity: Entity) => boolean),
    action = this.storeOptions.name + ': remove'
  ): void {
    const ids = isFunction(idOrPredicate)
      ? this.store
          .entities()
          .filter(idOrPredicate)
          .map(entity => entity.id)
      : coerceArray(idOrPredicate)

    if (!ids.length) {
      if (isFunction(idOrPredicate)) {
        this.queueRestoreOperation(() => {
          this.replayRemoveOperation(idOrPredicate, action)
        })
      }

      return
    }

    this.integrateDevtools
      ? updateState(this.store, action, removeEntities(ids))
      : patchState(this.store, removeEntities(ids))

    this.emitTrackedEntitiesRemoved(ids, action)
    this.persistenceDelegate?.delete(ids)
    this.queueRestoreOperation(() => {
      this.replayRemoveOperation(idOrPredicate, action)
    })
  }

  /**
   * Removes entities the server confirmed as deleted or inaccessible and stops tracking them in
   * every sync filter. Use `remove` for local or optimistic removals: those keep the sync filter
   * membership, so a rollback restores the entity in filtered views and a lost entity stays
   * detectable by `EntitySignalQuery.requiresFilteredFullSync`.
   * @param {string | string[]} id
   * @param {string} [action=this.storeOptions.name + ': removeDeleted']
   */
  removeDeleted(id: string | string[], action = this.storeOptions.name + ': removeDeleted'): void {
    const ids = coerceArray(id)

    this.remove(ids, action)
    this.removeEntityIdsFromSyncFilters(ids)
  }

  /**
   * Emits `trackedEntitiesRemoved` when removed entities are still tracked by a sync filter.
   * @param {string[]} ids
   * @param {string} action Action name of the removal
   */
  private emitTrackedEntitiesRemoved(ids: string[], action: string): void {
    const removedEntityIds = new Set(ids)
    const syncFilters = this.syncFilters()
      .map(syncFilter => ({
        filterExpression: syncFilter.filterExpression,
        removedCount: syncFilter.entityIds.filter(entityId => removedEntityIds.has(entityId)).length,
        trackedCount: syncFilter.entityIds.length
      }))
      .filter(syncFilter => syncFilter.removedCount > 0)

    if (!syncFilters.length) {
      return
    }

    const trackedEntityIds = new Set(this.syncFilters().flatMap(syncFilter => syncFilter.entityIds))

    this.trackedEntitiesRemoved.next({
      action,
      entityIds: [...removedEntityIds].filter(entityId => trackedEntityIds.has(entityId)),
      syncFilters
    })
  }

  /**
   * Removes entity ids from all sync filters.
   * @param {string[]} ids
   */
  private removeEntityIdsFromSyncFilters(ids: string[]): void {
    const removedEntityIds = new Set(ids)
    const syncFilters = this.syncFilters()

    if (!syncFilters.some(syncFilter => syncFilter.entityIds.some(entityId => removedEntityIds.has(entityId)))) {
      return
    }

    this.setSyncFilters(
      syncFilters.map(syncFilter => ({
        ...syncFilter,
        entityIds: syncFilter.entityIds.filter(entityId => !removedEntityIds.has(entityId))
      }))
    )
  }

  /**
   * Syncs the entities with the store, adding, updating and removing entities as needed
   * @param {Entity[]} entitiesToSync
   * @param {Entity[]} storedEntities
   * @param {SyncOptions<Entity>} options
   */
  sync(entitiesToSync: Entity[], storedEntities: Entity[], options?: SyncOptions<Entity>): void {
    syncStore(entitiesToSync, storedEntities, this, options)
  }

  /**
   * Syncs a single entity with the store, adding or updating it as needed
   * @param {Entity} entity
   * @param {EntityProps<Entity>} [compareProp='resourceVersion']
   * @param {string} [action=this.storeOptions.name + ': syncEntity']
   */
  syncEntity(
    entity: Entity,
    compareProp: EntityProps<Entity> = 'resourceVersion',
    action = this.storeOptions.name + ': syncEntity'
  ): void {
    this.upsert(
      entity.id,
      storedEntity => {
        if (isStoredEntityOutdated(storedEntity, entity, compareProp)) {
          const nullableOverrides = getNullableOverrides(entity, this.storeOptions.nullableProps)

          return { ...entity, ...nullableOverrides }
        } else {
          return storedEntity
        }
      },
      action
    )
  }

  /**
   * Syncs the store with the API.
   * Stale responses, requested before the store was reset, are ignored (see `ApiSyncOptions`).
   * @param {SyncResponse<Entity>} syncResponse
   * @param {ApiSyncOptions} options
   */
  apiSync(syncResponse: SyncResponse<Entity>, options?: ApiSyncOptions): void {
    // The store was reset after this response was requested, so the response belongs to discarded state
    if (options?.issuedAtGeneration !== undefined && options.issuedAtGeneration !== this.resetGeneration) {
      return
    }

    // A sync response can only be requested from a loaded store with a last sync date. If that is
    // no longer the case, the store was reset while the request was in flight, and applying the
    // response would wrongly mark the now-incomplete store as fully loaded.
    if (!options?.skipStaleCheck && (this.isLoading() || !this.lastSync())) {
      return
    }

    if (syncResponse.updated.length) {
      this.upsertMany(syncResponse.updated)
    }

    if (syncResponse.deleted.length) {
      this.removeDeleted(syncResponse.deleted, this.storeOptions.name + ': apiSync - deleted')
    }

    if (syncResponse.inaccessible?.length) {
      this.removeDeleted(syncResponse.inaccessible, this.storeOptions.name + ': apiSync - inaccessible')
    }

    if (options?.updateLastSync !== false) {
      this.setLastSync(syncResponse.lastSync)
    }

    this.setLoading(false)
  }

  /**
   * Syncs filtered API results with the store and updates only the filter-specific sync state.
   * The store-wide lastSync remains unchanged.
   * Stale responses, requested before the store was reset, are ignored (see `ApiSyncOptions`).
   * @param {string} filterExpression
   * @param {SyncResponse<Entity>} syncResponse
   * @param {ApiSyncOptions} options
   */
  apiSyncFiltered(filterExpression: string, syncResponse: SyncResponse<Entity>, options?: ApiSyncOptions): void {
    // The store was reset after this response was requested, so the response belongs to discarded state
    if (options?.issuedAtGeneration !== undefined && options.issuedAtGeneration !== this.resetGeneration) {
      return
    }

    // A filtered sync response can only be requested while its sync filter exists with a last sync
    // date. If that is no longer the case, the store was reset while the request was in flight.
    if (!options?.skipStaleCheck) {
      const syncFilter = this.syncFilters().find(entity => entity.filterExpression === filterExpression)

      if (!syncFilter || !syncFilter.lastSync) {
        return
      }
    }

    // The staleness decision is already taken at the filtered level, the store-wide loading state
    // and last sync date are irrelevant for filtered stores
    this.apiSync(syncResponse, { updateLastSync: false, skipStaleCheck: true })
    this.upsertSyncFilter(filterExpression, syncResponse)
  }

  /**
   * Sets a sync filter from an authoritative full fetch.
   * Keeps a missing server timestamp empty so the next request performs a full fetch.
   * The response holds the complete entity set of the filter, so the tracked entity ids are replaced.
   * `upsertSyncFilter` merges them instead, which would keep ids of a previous, partial cache alive.
   * @param {string} filterExpression
   * @param {SyncResponse<Entity>} syncResponse
   */
  setSyncFilter(filterExpression: string, syncResponse: SyncResponse<Entity>): void {
    const syncFilter: SyncFilter = {
      filterExpression,
      entityIds: syncResponse.updated.map(entity => entity.id),
      lastSync: syncResponse.lastSync
    }
    const syncFilters = this.syncFilters()
    const hasSyncFilter = syncFilters.some(entry => entry.filterExpression === filterExpression)

    this.setSyncFilters(
      hasSyncFilter
        ? syncFilters.map(entry => (entry.filterExpression === filterExpression ? syncFilter : entry))
        : [...syncFilters, syncFilter]
    )
  }

  /**
   * Upserts a sync filter
   * @param {string} filterExpression
   * @param {SyncResponse<Entity>} syncResponse
   */
  upsertSyncFilter(filterExpression: string, syncResponse: SyncResponse<Entity>): void {
    const globallyRemovedEntityIds = new Set([...syncResponse.deleted, ...(syncResponse.inaccessible || [])])
    const syncFilters = this.syncFilters().map(syncFilter => ({
      ...syncFilter,
      entityIds: syncFilter.entityIds.filter(entityId => !globallyRemovedEntityIds.has(entityId))
    }))
    const syncFilter = syncFilters.find(entity => entity.filterExpression === filterExpression)

    const syncEntityIds = syncResponse.updated.map(entity => entity.id)
    const removedEntityIds = new Set([
      ...(syncResponse.unfilteredUpdated || []),
      ...syncResponse.deleted,
      ...(syncResponse.inaccessible || [])
    ])
    const lastSync = syncResponse.lastSync || new Date()

    if (syncFilter) {
      const uniqueEntityIds = [...new Set([...syncFilter.entityIds, ...syncEntityIds])]
      const filteredEntityIds = uniqueEntityIds.filter(id => !removedEntityIds.has(id))

      const updatedSyncFilter = { filterExpression, entityIds: filteredEntityIds, lastSync }
      const updatedSyncFilters = syncFilters.map(entity =>
        entity.filterExpression === syncFilter.filterExpression ? updatedSyncFilter : entity
      )

      this.setSyncFilters(updatedSyncFilters)
    } else {
      this.setSyncFilters([...syncFilters, { filterExpression, entityIds: syncEntityIds, lastSync }])
    }
  }

  /**
   * Removes the sync filter which contains the given filter expression
   * @param {string} filterExpression
   */
  removeSyncFilter(filterExpression: string): void {
    if (!this.syncFilters()?.length) {
      return
    }

    const syncFilters = this.syncFilters().filter(syncFilter => syncFilter.filterExpression !== filterExpression)

    this.setSyncFilters(syncFilters)
  }

  /**
   * Removes sync filter entries that have a lastSync date older than the threshold
   * This is called automatically on initialization to keep the store clean
   */
  performSyncFiltersCleanup(): void {
    untracked(() => {
      const now = new Date()
      const thresholdDate = new Date(now.getTime() - SYNC_FILTERS_CLEANUP_THRESHOLD * 24 * 60 * 60 * 1000)

      const syncFilters =
        this.syncFilters()?.filter(syncFilter => {
          if (!syncFilter.lastSync) {
            return false
          }

          const lastSync = new Date(syncFilter.lastSync)

          return lastSync > thresholdDate
        }) || []

      this.setSyncFilters(syncFilters)
    })
  }

  /**
   * Resets the store by removing all entities
   * @param {string} [action=this.storeOptions.name + ': reset']
   */
  reset(action = this.storeOptions.name + ': reset'): void {
    this.resetGeneration++
    this.shouldRestorePersistedState = false
    this.clearPersistedStore()

    this.integrateDevtools
      ? updateState(this.store, action, removeAllEntities())
      : patchState(this.store, removeAllEntities())

    this.activeId.set(null)
    this.setLoading(true)
    this.error.set(null)
    this.lastSync.set(null)
    this.syncFilters.set([])
    this.pendingRestoreOperations = []
    this.persistenceDelegate?.reset()
  }

  /**
   * Clears the persisted store entry immediately from IndexedDB.
   * This bypasses debounced persistence and ensures reset state is applied right away.
   */
  private clearPersistedStore(): void {
    const storage = PersistState.instance?.storage

    if (!this.storeOptions.persist || !isPersistStateEntityDeleteStorage(storage)) {
      return
    }

    storage.deleteEntityStore(this.storeOptions.name).subscribe({
      error: () => {}
    })
  }

  /**
   * Queues a local row operation so it can be replayed after restore.
   * @param {() => void} replayOperation
   */
  private queueRestoreOperation(replayOperation: () => void): void {
    if (this.hasCompletedRestoreTracking || this.isRestoreHydrating) {
      return
    }

    this.pendingRestoreOperations.push(replayOperation)
  }

  /**
   * Replays a queued entity update after restore completed.
   * @param {string | string[] | ((entity: Entity) => boolean)} idOrPredicate
   * @param {Partial<Entity> | ((entity: Entity) => Partial<Entity>)} newStateOrFn
   * @param {string} action
   * @param {boolean} useNullableProps
   */
  private replayUpdateOperation(
    idOrPredicate: string | string[] | ((entity: Entity) => boolean),
    newStateOrFn: Partial<Entity> | ((entity: Entity) => Partial<Entity>),
    action?: string,
    useNullableProps?: boolean
  ): void {
    this.update(idOrPredicate as never, newStateOrFn as never, action, useNullableProps)
  }

  /**
   * Replays a queued entity removal after restore completed.
   * @param {string | string[] | ((entity: Entity) => boolean)} idOrPredicate
   * @param {string} action
   */
  private replayRemoveOperation(
    idOrPredicate: string | string[] | ((entity: Entity) => boolean),
    action?: string
  ): void {
    this.remove(idOrPredicate as never, action)
  }
}
